import { defineComponent as _defineComponent } from 'vue'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const __default__ = require('@nuxtjs/composition-api').defineComponent({
  head() {
    return {
      script: [
        {
          vmid: 'recurly',
          hid: 'recurlyjs',
          src: 'https://js.recurly.com/v4/recurly.js',
          defer: true,
          callback: () => {
            window.recurly.configure({
              publicKey: this.$config.recurlyPublicKey,
              currency: this.$store.state?.location?.currency?.toUpperCase(),
            });
            // @ts-ignore
            window.recurly.ready(() => this.initRecurly());
          },
        },
      ],
    };
  },
});

import {
  computed,
  onMounted,
  useStore,
  ref,
  useRouter,
  useContext,
  watch,
} from '@nuxtjs/composition-api';
import { CheckoutPrice, RecurlyError, TokenPayload } from '@recurly/recurly-js';
import axios, { AxiosError } from 'axios';
import Vue from 'vue';
import ReferralCodeInput from '~/components/upgrade/ReferralCodeInput.vue';
import DiscountInput from '~/components/upgrade/DiscountInput.vue';
import BillingAddressForm from '~/components/upgrade/BillingAddressForm.vue';
import PlanSelector from '~/components/upgrade/PlanSelector.vue';
import { useRecurly } from '~/composables/useRecurly';
import { State } from '~/store';
import { User } from '~/types/user';
import PaymentBadges from '~/components/upgrade/PaymentBadges.vue';
import LabelledHr from '~/components/atoms/LabelledHR.vue';
import { Plan, isRecurlyPlanCodeForLocation } from '~/types/plan';
import { plans } from '~/data/plans';
import { useUpgrade, Address } from '~/composables/useUpgrade';
import { useSaasquatch } from '~/composables/useSaasquatch';
import { useCredits } from '~/composables/useCredits';
import { useTrackSignup } from '~/composables/useTrackSignUp';
import { usePaymentMethods } from '~/composables/usePaymentMethods';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'UpgradeUser',
  setup(__props, { expose }) {

const {
  isPurchaseLoading,
  coupon,
  couponError,
  giftCard,
  giftCardError,
  paymentMethod,
  billingAddress,
  selectedPlan,
  selectedPlanCode,
  purchaseNewSubscription,
  upgradeSubscription,
  getPriceDiffForPlan,
} = useUpgrade();

const paymentMethods = usePaymentMethods();

const { getPlanName } = useTrackSignup();

const { getTotalCreditAmount, totalCreditAmount } = useCredits();

const store = useStore<State>();
const location = computed(() => store.state.location);
const isSubscribing = computed(() => store.state.currentPlan);
const loggedInUser = computed<User>(() => store.getters.loggedInUser);
const usersSubscriptionCode = computed(() => loggedInUser.value?.subscription?.code);
const userSubscriptionName = computed(() => loggedInUser.value?.subscription?.name);

const planSelectorClass = computed(() => {
  if (isSubscribing) {
    return 'tw-hidden';
  }
});

const router = useRouter();

const paidPlans = plans[location.value.secondary].filter(plan => plan.code !== 'free') as Plan[];
// Slug is the recurly plan code
const paidPlanCodes = paidPlans.map((plan: Plan) => plan.slug);

const price = ref(0);
const formError = ref('');
const recurlyFormError = ref<RecurlyError | null>(null);
const currentPlanDiff = ref<null | string>(null);
const isUserUpgradingExistingPlan = computed(
  () => loggedInUser.value?.subscription?.code === 'free'
);

const planSelect = ref<HTMLElement | null>(null);
const upgradeForm = ref<HTMLFormElement | null>(null);
const errorEl = ref<HTMLElement | null>(null);
const recurlyCard = ref<HTMLElement | null>(null);
const recurlyCheckout = ref<HTMLElement | null>(null);
const referralDiscount = ref<number>(0);
const isReferralCodeValidated = ref(false);
const referralStatusRefreshKey = 'referral-status-refresh-needed';

const { $config, $isFlagOn, localePath, $mzAnalyticsApi } = useContext();
const {
  recurlyError,
  init,
  getCCToken,
  startPayPalCheckout,
  paypalInstance,
  isInitialising,
  currentPricing,
} = useRecurly();

const shouldShowReferralInput = computed(() => {
  if (!$isFlagOn('saasquatch-integration')) {
    return false;
  }
  const country = location.value.code;
  const plan = selectedPlanCode.value;
  if (country === 'au' && plan === 'plus_annual') {
    return true;
  }
  if (country === 'gb' && plan === 'plus_annual') {
    return true;
  }
  if (country === 'us' && plan === 'plus_annual') {
    return true;
  }
  return false;
});

const { referralCode, referralCodeError, validateReferralCode } = useSaasquatch();

const getCurrentPricingTotal = (): number => {
  const total = Number(currentPricing.value?.now.total);
  return Number.isFinite(total) ? total : 0;
};

const toCents = (amount: number): number => Math.round(amount * 100);
const fromCents = (cents: number): number => cents / 100;

const recalculateReferralDiscount = () => {
  if (!isReferralCodeValidated.value) {
    referralDiscount.value = 0;
    return;
  }
  const totalCents = toCents(getCurrentPricingTotal());
  const discountCents = totalCents > 0 ? Math.round(totalCents * 0.1) : 0;
  referralDiscount.value = fromCents(discountCents);
};

const adjustedTotalNow = computed(() => {
  const totalCents = toCents(getCurrentPricingTotal());
  const discountCents = toCents(referralDiscount.value);
  const creditCents = toCents(totalCreditAmount.value);
  const adjustedCents = Math.max(totalCents - discountCents - creditCents, 0);
  return fromCents(adjustedCents).toFixed(2);
});

watch(referralCodeError, (error: string | undefined) => {
  isPurchaseLoading.value = false;
  if (error) {
    formError.value = error;
  }
});

watch(referralCode, async (code: string | undefined) => {
  isReferralCodeValidated.value = false;
  if (code) {
    const isValid = await validateReferralCode();
    if (isValid) {
      isReferralCodeValidated.value = true;
      formError.value = '';
      recalculateReferralDiscount();
      return;
    }
    referralDiscount.value = 0;
  }
  referralDiscount.value = 0;
});

watch(currentPricing, () => {
  recalculateReferralDiscount();
});

watch(recurlyError, (error: RecurlyError | null) => {
  isPurchaseLoading.value = false;
  if (error) {
    if (error.code === 'not-found') {
      // Not ideal to do string matching but recurly doesn't expose
      // a specific error code so we can map it to the correct input field
      if (error.message.toLowerCase().includes('coupon')) {
        couponError.value = error.message;
      } else if (error.message.toLowerCase().includes('gift card')) {
        giftCardError.value = error.message;
      }
    } else {
      formError.value = error.message;
    }
  }
});

const updateAddress = (_address: Address) => {
  billingAddress.value = _address;
};

const markReferralStatusForRefresh = () => {
  if (typeof window === 'undefined' || !isReferralCodeValidated.value) {
    return;
  }
  window.sessionStorage.setItem(referralStatusRefreshKey, '1');
};

onMounted(() => {
  if (!loggedInUser.value) {
    router.push('/login');
  }
  // Vue 2 has no top level await, but this is fine without an await
  (async () => {
    if ($isFlagOn('show-credits-on-checkout')) {
      await getTotalCreditAmount(loggedInUser.value?.id);
    }
  })();

  $mzAnalyticsApi({
    event: 'pageView',
  });
});

watch([referralCode, coupon, giftCard, paymentMethod, billingAddress], () => {
  formError.value = '';
  recurlyFormError.value = null;
  recurlyError.value = null;
});

const onPricingChange = async (plan: CheckoutPrice) => {
  if (usersSubscriptionCode.value !== 'free') {
    price.value = await getPriceDiffForPlan(selectedPlanCode.value);
    if (selectedPlan.value?.price) {
      const diff = Math.abs(Number(price.value) - Number(selectedPlan.value?.price.value));
      currentPlanDiff.value = diff.toFixed(2);
    }
  }
};

const initRecurly = () => {
  if (!recurlyCard.value || !recurlyCheckout.value) {
    console.error('Required recurly card element not found');
    formError.value =
      'There was an issue loading the payment form. Please reload or try again later.';
    return;
  }

  init(recurlyCard.value, recurlyCheckout.value, onPricingChange);
  console.log('init recurly', paypalInstance.value);
  if (paypalInstance.value) {
    paypalInstance.value.on('token', async (token: TokenPayload) => {
      isPurchaseLoading.value = true;
      const referralCodeToSend = isReferralCodeValidated.value ? referralCode.value : undefined;
      await purchaseNewSubscription(
        token,
        selectedPlanCode.value,
        currentPricing.value?.now.total,
        referralCodeToSend
      );
      markReferralStatusForRefresh();
      store.dispatch('setReferralCode', null);
      router.push({ path: '/hub/', query: { account_action: 'upgrade' } });
    });
  }
};

const planSelected = async (plan: string) => {
  if (isRecurlyPlanCodeForLocation(plan, location.value.secondary)) {
    selectedPlanCode.value = plan;
    // Wait for DOM value update before triggering event
    await Vue.nextTick();

    // Manually trigger change event so Recurly knows it's been updated
    const event = new Event('change');
    planSelect.value?.dispatchEvent(event);
  }
};

const setAndScrollToError = async (error: string | Error | AxiosError) => {
  if (axios.isAxiosError(error)) {
    formError.value = error.response?.data?.message || error.message;
  } else if (error instanceof Error) {
    formError.value = error.message;
  } else {
    formError.value = error;
  }
  isPurchaseLoading.value = false;
  await Vue.nextTick();
  if (errorEl.value) {
    errorEl.value.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }
};

const subscribe = async () => {
  isPurchaseLoading.value = true;

  let name = 'upgrade_offer.upgrade_to_' + selectedPlanCode.value.replace('_', '-').toLowerCase();
  if (paymentMethod.value === 'paypal') {
    name += '.checkout_with_paypal';
  } else if (usersSubscriptionCode.value !== 'free') {
    name += '.upgrade';
  } else {
    name += '.subscribe';
  }

  $mzAnalyticsApi({
    event: 'click',
    name,
    productArea: 'profile',
  });

  // paid -> paid. We used stored payment method here
  if (usersSubscriptionCode.value !== 'free') {
    const referralCodeForUpgrade = isReferralCodeValidated.value ? referralCode.value : undefined;
    await upgradeSubscription(
      selectedPlanCode.value,
      price.value.toFixed(2),
      referralCodeForUpgrade
    );
    markReferralStatusForRefresh();
    store.dispatch('setReferralCode', null);
    router.push({ path: '/hub/', query: { account_action: 'upgrade' } });
    return;
  }

  const missingBillingFields = !Object.values(billingAddress.value).every(x => x);
  if (missingBillingFields) {
    const missingKeys = Object.entries(billingAddress.value)
      .filter(([_, v]) => !v)
      .map(([k]) => k);
    await setAndScrollToError(
      'Please fill in all address fields. Missing fields: ' + missingKeys.join(', ')
    );
    return;
  }

  switch (paymentMethod.value) {
    case 'credit-card':
      if (!upgradeForm.value) {
        await setAndScrollToError(
          'There was an issue loading the payment form. Please reload or try again later.'
        );
        return;
      }
      try {
        const referralCodeToSend = isReferralCodeValidated.value ? referralCode.value : undefined;
        await purchaseNewSubscription(
          await getCCToken(upgradeForm.value),
          selectedPlanCode.value,
          currentPricing.value?.now.total,
          referralCodeToSend
        );
        markReferralStatusForRefresh();
        store.dispatch('setReferralCode', null);
        if (isSubscribing.value) {
          router.push({ path: localePath('/signup-complete') });
        } else {
          router.push({ path: '/hub/', query: { account_action: 'upgrade' } });
        }
      } catch (e: any) {
        console.error(e);
        if (e.code === 'validation') {
          const error = e as RecurlyError;
          recurlyFormError.value = error;
        }
        await setAndScrollToError('There was an issue processing your payment.');
      }
      break;
    case 'paypal':
      startPayPalCheckout();
      isPurchaseLoading.value = false;
      break;
    default:
      console.error('Unknown payment method', paymentMethod.value);
      formError.value = 'Unknown payment method ' + paymentMethod.value;
      break;
  }
  isPurchaseLoading.value = false;
};

// Exposing these allows us to use them in the normal script tag
expose({
  $config,
  initRecurly,
});

return { __sfc: true,isPurchaseLoading, coupon, couponError, giftCard, giftCardError, paymentMethod, billingAddress, selectedPlan, selectedPlanCode, purchaseNewSubscription, upgradeSubscription, getPriceDiffForPlan, paymentMethods, getPlanName, getTotalCreditAmount, totalCreditAmount, store, location, isSubscribing, loggedInUser, usersSubscriptionCode, userSubscriptionName, planSelectorClass, router, paidPlans, paidPlanCodes, price, formError, recurlyFormError, currentPlanDiff, isUserUpgradingExistingPlan, planSelect, upgradeForm, errorEl, recurlyCard, recurlyCheckout, referralDiscount, isReferralCodeValidated, referralStatusRefreshKey, $config, $isFlagOn, localePath, $mzAnalyticsApi, recurlyError, init, getCCToken, startPayPalCheckout, paypalInstance, isInitialising, currentPricing, shouldShowReferralInput, referralCode, referralCodeError, validateReferralCode, getCurrentPricingTotal, toCents, fromCents, recalculateReferralDiscount, adjustedTotalNow, updateAddress, markReferralStatusForRefresh, onPricingChange, initRecurly, planSelected, setAndScrollToError, subscribe, ReferralCodeInput, DiscountInput, BillingAddressForm, PlanSelector, PaymentBadges, LabelledHr }
}

})